/**
 * WorkTask Inbox Service
 * Custom service for Task Inbox operations (not auto-generated).
 * Wraps the custom backend endpoints: my-tasks, claim, unclaim, complete, cancel, inbox-count.
 */

import { WorkTask } from '@/types/taskmanagement/WorkTask';
import { apiClient } from '@/lib/apiClient';
import type { AbpPagedResponse } from '@/lib/gridDataSourceUtils';

export interface CompleteWorkTaskInput {
  outcome: string;
  comment?: string;
}

export interface TaskInboxCount {
  myTasksCount: number;
  overdueCount: number;
}

export interface MyTasksParams {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
}

class WorkTaskInboxService {
  private readonly baseUrl = '/api/taskmanagement/worktask';

  /**
   * Get tasks for the current user's inbox.
   * Returns tasks assigned to, claimed by, or claimable by the current user.
   */
  async getMyTasks(params?: MyTasksParams): Promise<AbpPagedResponse<WorkTask>> {
    const response = await apiClient.get<AbpPagedResponse<WorkTask>>(`${this.baseUrl}/my-tasks`, {
      params: {
        skipCount: params?.skipCount ?? 0,
        maxResultCount: params?.maxResultCount ?? 50,
        sorting: params?.sorting,
      },
    });
    return response.data;
  }

  /**
   * Get tasks related to a specific entity.
   */
  async getEntityTasks(entityType: string, entityId: string): Promise<AbpPagedResponse<WorkTask>> {
    const response = await apiClient.get<AbpPagedResponse<WorkTask>>(
      `${this.baseUrl}/entity/${entityType}/${entityId}`
    );
    return response.data;
  }

  /**
   * Get inbox task counts for the navigation badge.
   */
  async getInboxCount(): Promise<TaskInboxCount> {
    const response = await apiClient.get<TaskInboxCount>(`${this.baseUrl}/inbox-count`);
    return response.data;
  }

  /**
   * Claim a pending task for the current user.
   */
  async claim(id: string): Promise<WorkTask> {
    const response = await apiClient.post<WorkTask>(`${this.baseUrl}/${id}/claim`);
    return response.data;
  }

  /**
   * Release a claimed task back to the pool.
   */
  async unclaim(id: string): Promise<WorkTask> {
    const response = await apiClient.post<WorkTask>(`${this.baseUrl}/${id}/unclaim`);
    return response.data;
  }

  /**
   * Complete a task with an outcome (resumes the workflow).
   */
  async complete(id: string, input: CompleteWorkTaskInput): Promise<WorkTask> {
    const response = await apiClient.post<WorkTask>(`${this.baseUrl}/${id}/complete`, input);
    return response.data;
  }

  /**
   * Cancel a task.
   */
  async cancel(id: string): Promise<WorkTask> {
    const response = await apiClient.post<WorkTask>(`${this.baseUrl}/${id}/cancel`);
    return response.data;
  }
}

export const workTaskInboxService = new WorkTaskInboxService();
